import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Spinner,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useRef, useState } from 'react';
import { RiAddCircleLine } from 'react-icons/ri';
import { HelivanButton } from '../helivan/HelivanButton';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanText } from '../helivan/HelivanText';
import {
  useGetMyOrganizationsWithRolesQuery,
  useSetUserOrganizationMutation,
  useLeaveOrganizationMutation,
} from '../../generated/graphql';
import { useAuth } from '../../contexts/AuthContext';
import { CreateOrganizationModal } from './CreateOrganizationModal';

const getRoleBadgeColor = (role: string) => {
  switch (role) {
    case 'OWNER':
      return 'purple';
    case 'ADMIN':
      return 'blue';
    default:
      return 'gray';
  }
};

export const OrganizationsList = () => {
  const toast = useToast();
  const { backendUser, refreshBackendUser } = useAuth();
  const { data, loading, refetch } = useGetMyOrganizationsWithRolesQuery();
  const [setUserOrganization, { loading: switching }] =
    useSetUserOrganizationMutation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isLeaveOpen,
    onOpen: onLeaveOpen,
    onClose: onLeaveClose,
  } = useDisclosure();
  const [leaveOrganization, { loading: leaving }] =
    useLeaveOrganizationMutation();
  const [orgToLeave, setOrgToLeave] = useState<{ id: string; name: string } | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const handleSwitchOrganization = async (orgId: string) => {
    try {
      await setUserOrganization({
        variables: { orgId },
      });
      await refreshBackendUser();
      toast({
        title: 'Organization switched',
        status: 'success',
        duration: 2000,
      });
    } catch (error) {
      toast({
        title: 'Error switching organization',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleCreateSuccess = () => {
    refetch();
  };

  const handleLeaveClick = (org: { id: string; name: string }) => {
    setOrgToLeave(org);
    onLeaveOpen();
  };

  const handleLeaveOrganization = async () => {
    if (!orgToLeave) return;
    try {
      await leaveOrganization({
        variables: { organizationId: orgToLeave.id },
      });
      await refreshBackendUser();
      refetch();
      toast({
        title: 'Left organization',
        description: `You have left ${orgToLeave.name}`,
        status: 'success',
        duration: 2000,
      });
      onLeaveClose();
      setOrgToLeave(null);
    } catch (error) {
      toast({
        title: 'Error leaving organization',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 3000,
      });
    }
  };

  if (loading) {
    return (
      <Flex justify="center" py={8}>
        <Spinner color={HelivanColor.text.helivanBlue} />
      </Flex>
    );
  }

  const organizations = data?.getMyOrganizationsWithRoles || [];

  return (
    <VStack spacing={4} align="start" width="full">
      <Flex justify="flex-end" width="full" align="center">
        <HelivanButton variant="green" size="sm" onClick={onOpen}>
          <HStack spacing={1}>
            <RiAddCircleLine color="white" size={14} />
            <HelivanText colorOverride="white" variant="smallBody">
              Create Organization
            </HelivanText>
          </HStack>
        </HelivanButton>
      </Flex>

      {organizations.length === 0 ? (
        <Box py={8} width="full" textAlign="center">
          <HelivanText variant="smallBody">
            You are not a member of any organizations yet.
          </HelivanText>
        </Box>
      ) : (
        <Box width="full" overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead bg={HelivanColor.background.lightestGrey}>
              <Tr>
                <Th borderColor={HelivanColor.border.primary}>
                  <HelivanText variant="smallBody+" colorOverride={HelivanColor.text.blueMid}>
                    Organization
                  </HelivanText>
                </Th>
                <Th borderColor={HelivanColor.border.primary}>
                  <HelivanText variant="smallBody+" colorOverride={HelivanColor.text.blueMid}>
                    Type
                  </HelivanText>
                </Th>
                <Th borderColor={HelivanColor.border.primary}>
                  <HelivanText variant="smallBody+" colorOverride={HelivanColor.text.blueMid}>
                    Your Role
                  </HelivanText>
                </Th>
                <Th borderColor={HelivanColor.border.primary}>
                  <HelivanText variant="smallBody+" colorOverride={HelivanColor.text.blueMid}>
                    Actions
                  </HelivanText>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {organizations.map((org) => {
                const isCurrent =
                  backendUser?.currentOrganization?.id === org.id;
                return (
                  <Tr key={org.id} _hover={{ bg: HelivanColor.background.lightestGrey }}>
                    <Td borderColor={HelivanColor.border.primary}>
                      <HStack>
                        <HelivanText
                          variant="smallBody"
                          colorOverride={HelivanColor.text.black}
                        >
                          {org.name}
                        </HelivanText>
                        {isCurrent && (
                          <Badge colorScheme="green" fontSize="10px">
                            Current
                          </Badge>
                        )}
                      </HStack>
                    </Td>
                    <Td borderColor={HelivanColor.border.primary}>
                      <HelivanText variant="smallBody">
                        {org.type === 'PERSONAL' ? 'Personal' : 'Organization'}
                      </HelivanText>
                    </Td>
                    <Td borderColor={HelivanColor.border.primary}>
                      <Badge colorScheme={getRoleBadgeColor(org.role)} fontSize="10px">
                        {org.role}
                      </Badge>
                    </Td>
                    <Td borderColor={HelivanColor.border.primary}>
                      <HStack spacing={2}>
                        {!isCurrent && (
                          <HelivanButton
                            variant="outline"
                            size="xs"
                            onClick={() => handleSwitchOrganization(org.id)}
                            isLoading={switching}
                          >
                            Switch
                          </HelivanButton>
                        )}
                        {org.role !== 'OWNER' && org.type !== 'PERSONAL' && (
                          <HelivanButton
                            variant="outline"
                            size="xs"
                            onClick={() => handleLeaveClick({ id: org.id, name: org.name })}
                            isLoading={leaving}
                          >
                            Leave
                          </HelivanButton>
                        )}
                      </HStack>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}

      <CreateOrganizationModal
        isOpen={isOpen}
        onClose={onClose}
        onSuccess={handleCreateSuccess}
      />

      <AlertDialog
        isOpen={isLeaveOpen}
        leastDestructiveRef={cancelRef}
        onClose={onLeaveClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Leave Organization
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to leave {orgToLeave?.name}? You will lose
              access to all resources in this organization.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onLeaveClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleLeaveOrganization}
                ml={3}
                isLoading={leaving}
              >
                Leave
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </VStack>
  );
};
